import { Customer_Contacts, Requirements_EmailIsCorrespondence } from 'data/endpoints';
import ContactDTO from 'features/customer/dtos/ContactDTO';
import IRequirementCreateService from 'features/requirements/services/IRequirementCreateService';
import PageCard from 'features/shared/components/card/PageCard';
import PagedDataResultDTO from 'features/shared/dtos/PagedDataResultDTO';
import FormGroup from 'features/shared/forms/components/FormGroup';
import { useForm } from 'features/shared/forms/hooks/useForm';
import { getRouteParams } from 'features/shared/helpers/routeParams';
import { useInjection } from 'infrastructure/ioc/ioc.react';
import { IocTypes } from 'infrastructure/ioc/ioc.types';
import CustomerState from 'infrastructure/store/customer/CustomerState';
import IAppState from 'infrastructure/store/IAppState';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import BasicInfoSubFormSection from '../basicInfoSubForm/BasicInfoSubFormSection';
import { emailFormValidations } from './emailFormValidation';
import { useQuery } from 'data/hooks/useQuery';
import DataQuery from 'data/DataQuery';
import { getCustomerContactsParams } from 'features/shared/helpers/queryParams';
import { useLocation } from 'react-router-dom';
import BaseRequirementFormProps from 'features/requirements/models/BaseRequirementFormProps';
import RenderWrapper from 'features/shared/components/renderWrapper/RenderWrapper';
import PostResponse from 'data/PostResponse';
import { RequirementResultDialogParams } from 'features/requirements/models/RequirementResultDialogParams';
import { openRequirementResultDialog } from 'features/requirements/helpers/requirementResultDialogHelper';
import InsertRequirementResultDTO from 'features/requirements/dtos/InsertRequirementResultDTO';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import reactSelectStyles from "features/shared/components/reactSelect/reactselectcustom.module.scss";
import Alert from 'features/shared/alerts/components/Alert';
import { EmaiFormSection_EmailCard } from 'features/shared/components/loaders/promiseLoadingAreas';

interface Props extends BaseRequirementFormProps {
}

const EmailFormSection = (props?: Props) => {
  // translations
  const tLabels = useTranslation("requirements\\requirements").t;

  // DI
  const createService = useInjection<IRequirementCreateService>(IocTypes.RequirementCreateService);

  // router location
  const { state: emailContactLocation  } = useLocation<ContactDTO>();

  // props
  const useOutsideOfRequirements = props?.useOutsideOfRequirements ? true : false;
  const sendButtonTitle = props?.sendButtonTitle ? props.sendButtonTitle : tLabels("emailFormSection.sendRequirement");
  const successMessage: string = useOutsideOfRequirements ? tLabels("emailFormSection.successMessageOutsideRequirement") : tLabels("emailFormSection.successMessage");
  const errorMessage: string | undefined = useOutsideOfRequirements ? tLabels("emailFormSection.errorMessage") : undefined;

  // store
  const customer = (useSelector<IAppState, CustomerState>((store) => store.customer)).customer;

  // state
  const [selectedEmail, setSelectedEmail] = useState<ContactDTO | null>(null);

  // form hook
  const { values, errors, bindField, bindUncontrolledField, valideteForm, validateFormField, setValidationRule, setFormValue } = useForm({
    initialValidations: emailFormValidations()
  });

  // fetch data
  // emails
  // TODO: pokud bude mít zákazník > 500 emailů - nebude fungovat (aktuálně má zákazník na prod. max. 300 emailů)
  let emailQuery: DataQuery = {url: Customer_Contacts, routeParams: getRouteParams("customerId", customer.customerId!), params: getCustomerContactsParams("Email", 500)};
  const { data: emails } = useQuery<PagedDataResultDTO<ContactDTO>>(emailQuery, {count: 0, result: []}, [customer]);

  // correspondence email
  const correspondenceEmailQuery: DataQuery = {
    url: Requirements_EmailIsCorrespondence,
    params: new URLSearchParams({ SelectedEmail: selectedEmail?.value!, CustomerId: customer.customerId! })
  };

  const { data: isCorrespondenceEmail } = useQuery<boolean>(
    correspondenceEmailQuery,
    false,
    [values["DESC_TMEMAILPUVODVyBER"]],
    { useTracker: true, area: EmaiFormSection_EmailCard },
    { apply: true, condition: () => values["DESC_TMEMAILPUVODVyBER"] }
  );

  // side-effects
  useEffect(() => {
    // set selected email from location (from Customer identification page)
    if (emailContactLocation) {
      emailChangeHandler(emailContactLocation);
    }
  }, [emailContactLocation])

  // handlers
  const submitFormHandler = async (e: React.FormEvent) => {
    e.preventDefault();
    if (valideteForm()) {
      let result = await createService.createRequirement("ZMENA_EMAILU", values);
      openResultDialogHandler(result);
    }
  };

  const openResultDialogHandler = (result: PostResponse<InsertRequirementResultDTO>) => {
    const params: RequirementResultDialogParams = {
      response: result,
      successMessage: successMessage,
      errorMessage: errorMessage,
      actionBtn: { title: tLabels("emailFormSection.identificationData"), route: "/customer" },
      transitonBtn: null
    };

    openRequirementResultDialog(useOutsideOfRequirements, params, tLabels("emailFormSection.changeEmail"), true); 
  }

  const emailChangeHandler = (emailContact: ContactDTO | null) => { 
    if (emailContact && validateFormField("DESC_TMEMAILPUVODVyBER", emailContact.contactId)) {
      // set state
      setSelectedEmail(emailContact);
      // set select data to form values
      setFormValue("DESC_TMEMAILPUVODVyBER", emailContact.contactId);
    }   
  }

  // render
  return (
    <form onSubmit={submitFormHandler}>
      <PageCard 
        title={tLabels("emailFormSection.cardTitle")} 
        bodyClassName="pt-4 px-4"
        loadingAreaName={EmaiFormSection_EmailCard}
      >
        <RenderWrapper isVisible={() => isCorrespondenceEmail}>
            <Alert
              alertObj={{
                alertType: "Warning",
                message: tLabels("emailFormSection.correspondenceEmail"),
              }}
              className="mb-3"
            />
          </RenderWrapper>
          <div className="row no-gutters mx-n2">
            <div className="col-sm-6 px-2">
            <FormGroup
              labelText={tLabels("emailFormSection.originalEmail")}
              validationError={errors["DESC_TMEMAILPUVODVyBER"]}
            >
              <Select
                options={emails.result}
                getOptionValue={(email) => email.contactId!}
                getOptionLabel={(email) => email.value!}
                onChange={emailChangeHandler}
                value={selectedEmail}
                captureMenuScroll={false}
                className={reactSelectStyles.customSelect}
                classNamePrefix="customSelect"
                isSearchable
                placeholder={tLabels("emailFormSection.selectEmailForChange")}
                noOptionsMessage={() => tLabels("complaintSelect.noOptionsMessage")}
              />
            </FormGroup>
            </div>
            <div className="col-sm-6 px-2">
              <FormGroup labelText={tLabels("emailFormSection.newEmail")} inputId="DESC_TMEMAIL" validationError={errors["DESC_TMEMAIL"]}>
                <input id="DESC_TMEMAIL" className="form-control" placeholder={tLabels("emailFormSection.setNewEmail")} {...bindField("DESC_TMEMAIL")} />
              </FormGroup>
            </div>
          </div>       
      </PageCard>

      <RenderWrapper isVisible={() => !useOutsideOfRequirements}>
        <BasicInfoSubFormSection
          bindField={bindField}
          bindUncontrolledField={bindUncontrolledField}
          errors={errors}
          setValidationRule={setValidationRule}
        />
      </RenderWrapper>

      <button type="submit" className="btn btn-primary px-3 py-2">{sendButtonTitle}</button>
    </form>
  )
}

export default EmailFormSection;
